import create from 'zustand';
import type { AIGeneratedQuestion, GenerationStatus, PanelView } from './types';

interface AIQuestionBuilderState {
  answerCount: number | null;
  assessmentID: number | null;
  creationMode: 'create' | 'modify';
  error: string | null;
  generatedQuestions: AIGeneratedQuestion[];
  generationStatus: GenerationStatus;
  jobID: string | null;
  llmCompletionID: number | null;
  panelView: PanelView;
  questionCount: number | null;
  questions: Array<{ id: number; number_of_answers: number }> | null;
  rateLimited: boolean;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  selectedQuestionIDs: Set<number>;
  selectedSectionIDs: Set<number>;

  closePanel: () => void;
  openConfigurePanel: () => void;
  openModeSelectPanel: (showAiModify: boolean) => void;
  setAnswerCount: (value: number | null) => void;
  setAssessmentID: (id: number) => void;
  setCreationMode: (mode: 'create' | 'modify') => void;
  setQuestionCount: (value: number | null) => void;
  setQuestions: (questions: Array<{ id: number; number_of_answers: number }> | null) => void;
  reset: () => void;
  setError: (error: string | null) => void;
  setGeneratedQuestions: (questions: AIGeneratedQuestion[]) => void;
  setGenerationStatus: (status: GenerationStatus) => void;
  setJobID: (jobID: string | null) => void;
  setLlmCompletionID: (id: number | null) => void;
  setPanelView: (view: PanelView) => void;
  setRateLimited: (rateLimited: boolean) => void;
  setSaveStatus: (status: 'idle' | 'saving' | 'saved' | 'error') => void;
  toggleQuestionSelected: (questionID: number) => void;
  toggleSectionSelected: (sectionID: number) => void;
  selectAllQuestions: () => void;
  deselectAllQuestions: () => void;
  selectAllSections: (sectionIDs: number[]) => void;
  deselectAllSections: () => void;
}

const INITIAL_STATE = {
  answerCount: 4,
  assessmentID: null as number | null,
  creationMode: 'create' as 'create' | 'modify',
  error: null,
  generatedQuestions: [],
  generationStatus: 'idle' as GenerationStatus,
  jobID: null as string | null,
  llmCompletionID: null as number | null,
  panelView: 'closed' as PanelView,
  questionCount: 1,
  questions: null as Array<{ id: number; number_of_answers: number }> | null,
  rateLimited: false,
  saveStatus: 'idle' as 'idle' | 'saving' | 'saved' | 'error',
  selectedQuestionIDs: new Set<number>(),
  selectedSectionIDs: new Set<number>(),
};

const useAIQuestionBuilderStore = create<AIQuestionBuilderState>((set, get) => ({
  ...INITIAL_STATE,

  closePanel: () => set({ panelView: 'closed' }),

  deselectAllQuestions: () => set({ selectedQuestionIDs: new Set() }),

  deselectAllSections: () => set({ selectedSectionIDs: new Set() }),

  openConfigurePanel: () => set({
    error: null,
    generatedQuestions: [],
    generationStatus: 'idle',
    llmCompletionID: null,
    panelView: 'configure',
  }),

  openModeSelectPanel: (showAiModify: boolean) => {
    const { questions } = get();
    const hasQuestionsToModify = questions && questions.length > 0;

    if (hasQuestionsToModify && showAiModify) {
      set({
        error: null,
        generatedQuestions: [],
        generationStatus: 'idle',
        llmCompletionID: null,
        panelView: 'mode_select',
      });
    } else {
      set({
        creationMode: 'create',
        error: null,
        generatedQuestions: [],
        generationStatus: 'idle',
        llmCompletionID: null,
        panelView: 'configure',
      });
    }
  },

  reset: () => set(INITIAL_STATE),

  selectAllQuestions: () => {
    const allIDs = new Set(get().generatedQuestions.map(q => q.id));
    set({ selectedQuestionIDs: allIDs });
  },

  selectAllSections: sectionIDs => set({ selectedSectionIDs: new Set(sectionIDs) }),

  setAnswerCount: value => set({ answerCount: value }),

  setAssessmentID: id => set({ assessmentID: id }),

  setCreationMode: mode => set({ creationMode: mode }),

  setError: error => set({ error }),

  setGeneratedQuestions: (questions) => {
    const allIDs = new Set(questions.map(q => q.id));
    set({ generatedQuestions: questions, selectedQuestionIDs: allIDs });
  },

  setGenerationStatus: status => set({ generationStatus: status }),

  setJobID: jobID => set({ jobID }),

  setLlmCompletionID: id => set({ llmCompletionID: id }),

  setPanelView: view => set({ panelView: view }),

  setQuestionCount: value => set({ questionCount: value }),

  setQuestions: questions => set({ questions }),

  setRateLimited: rateLimited => set({ rateLimited }),

  setSaveStatus: status => set({ saveStatus: status }),

  toggleQuestionSelected: (questionID) => {
    const current = new Set(get().selectedQuestionIDs);
    if (current.has(questionID)) {
      current.delete(questionID);
    } else {
      current.add(questionID);
    }
    set({ selectedQuestionIDs: current });
  },

  toggleSectionSelected: (sectionID) => {
    const current = new Set(get().selectedSectionIDs);
    if (current.has(sectionID)) {
      current.delete(sectionID);
    } else {
      current.add(sectionID);
    }
    set({ selectedSectionIDs: current });
  },
}));

export default useAIQuestionBuilderStore;
